import { FC, useMemo } from 'react';

import * as ui from '@april9au/stack9-ui';
import { Form } from 'antd';

import { formatSlotDateTime } from '../../utils/datetime';

const S9Select = ui.Select.default;
const { useFormInstance, useWatch } = ui.S9Form;

interface AppointmentSlotSelectProps {
  eventId: number | undefined;
  /** Event timezone — slot instants are displayed as wall-clock time in it. */
  timezone?: string | null;
  disabled?: boolean;
}

export const AppointmentSlotSelect: FC<AppointmentSlotSelectProps> = ({
  eventId,
  timezone,
  disabled,
}) => {
  const form = useFormInstance();
  const slotId = useWatch(['slot_id'], form);

  const { data: slotsResponse, isValidating } = ui.useScreenQuery<{
    results: Array<{ id: number; start_dt: string; is_available?: boolean }>;
  }>('getappointmentslotsbyeventid', {
    vars: {
      id: eventId,
      page: 0,
      limit: 100,
      filters: JSON.stringify({}),
    },
    shouldFetch: !!eventId,
    filters: {
      is_available: { operation: 'eq', value: true },
    } as never,
  });

  const { data: selectedSlotData } = ui.useScreenQueryById<{
    id: number;
    start_dt: string;
  }>('getappointmentslotbyid', slotId, {
    shouldFetch:
      !!slotId && !slotsResponse?.results?.find(s => s.id === slotId),
  });

  const options = useMemo(() => {
    const availableOptions =
      slotsResponse?.results?.map(slot => ({
        label: formatSlotDateTime(slot.start_dt, timezone),
        value: slot.id,
      })) || [];

    if (
      selectedSlotData &&
      slotId &&
      !availableOptions.find(opt => opt.value === slotId)
    ) {
      return [
        {
          label: formatSlotDateTime(selectedSlotData.start_dt, timezone),
          value: selectedSlotData.id,
        },
        ...availableOptions,
      ];
    }

    return availableOptions;
  }, [slotsResponse, selectedSlotData, slotId, timezone]);

  return (
    <Form.Item
      label="Appointment Slot"
      name="slot_id"
      rules={[
        {
          required: true,
          message: 'Appointment slot is required for appointment bookings',
        },
      ]}
    >
      <S9Select
        style={{ width: '100%' }}
        showSearch
        options={options || []}
        loading={isValidating}
        disabled={disabled || !eventId}
        placeholder={
          !eventId ? 'Select an event first' : 'Select appointment slot'
        }
      />
    </Form.Item>
  );
};
